// Shared helpers for formatting estimates as professional commercial subcontractor proposals.
// Used by both EstimateDetailDrawer (customer view) and EstimatePrintTemplate.

export const DEFAULT_COMMERCIAL_EXCLUSIONS: string[] = [
  "Permits, fees, and plan review costs",
  "Bonds, insurance above standard limits, and builder's risk",
  "Engineering, architectural, and design services",
  "Unforeseen conditions, hazardous material remediation, or mold abatement",
  "Structural work, framing, demolition, or substrate preparation beyond standard surface prep",
  "Temporary power, water, lighting, or climate control",
  "After-hours, weekend, or holiday work unless expressly noted",
  "Sales, use, or excise taxes unless itemized in pricing summary",
];

export const DEFAULT_CLARIFICATIONS: string[] = [
  "Pricing based on work performed during normal business hours in a single mobilization unless noted.",
  "All quantities are approximate and subject to field verification.",
  "Access to work area must be clear, clean, and ready to receive our scope.",
  "Proposal valid for thirty (30) days from the date issued.",
];

export interface ProposalSections {
  scope: string;
  clarifications: string[];
  exclusions: string[];
  remainder: string;
}

/**
 * Parse a freeform notes field into structured proposal sections.
 * Looks for conventional headers (Scope:, Clarifications:, Exclusions:) and splits accordingly.
 * Any content before the first header is treated as scope summary.
 */
export function parseProposalNotes(notes: string | null | undefined): ProposalSections {
  const result: ProposalSections = {
    scope: "",
    clarifications: [],
    exclusions: [],
    remainder: "",
  };
  if (!notes || !notes.trim()) return result;

  const text = notes.trim();
  // Match section headers case-insensitively at line start
  const headerRegex = /^\s*(scope(?:\s+of\s+work)?|clarifications?|assumptions?|exclusions?|inclusions?)\s*[:\-]\s*$/im;

  const lines = text.split(/\r?\n/);
  let currentSection: "scope" | "clarifications" | "exclusions" | "remainder" = "scope";
  const buffers: Record<string, string[]> = { scope: [], clarifications: [], exclusions: [], remainder: [] };

  for (const line of lines) {
    const m = line.match(headerRegex);
    if (m) {
      const header = m[1].toLowerCase();
      if (header.startsWith("scope")) currentSection = "scope";
      else if (header.startsWith("clarif") || header.startsWith("assum") || header.startsWith("incl")) currentSection = "clarifications";
      else if (header.startsWith("excl")) currentSection = "exclusions";
      else currentSection = "remainder";
      continue;
    }
    buffers[currentSection].push(line);
  }

  result.scope = buffers.scope.join("\n").trim();
  result.clarifications = splitBulletList(buffers.clarifications.join("\n"));
  result.exclusions = splitBulletList(buffers.exclusions.join("\n"));
  result.remainder = buffers.remainder.join("\n").trim();
  return result;
}

/** Split a block of text into bullet items (by newline or leading bullet markers) */
function splitBulletList(block: string): string[] {
  if (!block.trim()) return [];
  return block
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*[-*•·]\s*/, "").trim())
    .filter((l) => l.length > 0);
}

/** Detect if a group should be treated as an alternate/add-deduct bid */
export function isAlternateGroup(name: string | null | undefined): boolean {
  if (!name) return false;
  return /\b(alt(ernate)?s?|add[/-]?deduct|optional?)\b/i.test(name);
}

/**
 * Extract a location hint from a line item description.
 * Looks for "Location:" prefix or known location keywords; otherwise returns a placeholder.
 */
export function parseLocation(description: string | null | undefined, fallback = "Per Plans"): string {
  if (!description) return fallback;
  const text = description.trim();

  // Explicit "Location:" pattern
  const locMatch = text.match(/\blocation\s*[:\-]\s*([^,;|\n]+)/i);
  if (locMatch) return locMatch[1].trim();

  // Room/area keywords at the end of description in parentheses or after em dash
  const roomMatch = text.match(/[—–\-()]\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s*\)?$/);
  if (roomMatch && /^(Room|Floor|Level|Wall|Ceiling|Kitchen|Bath|Lobby|Corridor|Entry|Exterior|Interior|Unit|Suite|Area|Zone|Phase)/i.test(roomMatch[1])) {
    return roomMatch[1].trim();
  }

  return fallback;
}

/** Format currency with consistent commercial-proposal style */
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}
